import { ASK_THREE_COPY } from "../lib/copy";
import type { ObserverView } from "../lib/types";
import { RaterInvites } from "./RaterInvites";

/**
 * Top of the results: ask three people who know you (Taylor: "so they don't miss it"; Edward,
 * Oct 31: "very prominent and clear, copy and send buttons, and why"). The links themselves are
 * here, each with Text, Share and Copy, under why it's worth it and how many have answered.
 * Hidden for takes without raters and once all three are in. The same links are also in "What
 * others see" further down.
 */
export function SendRatersNudge({
  observer,
  takeId,
  resultKey,
}: {
  observer: ObserverView | null | undefined;
  takeId: string;
  resultKey?: string | null;
}) {
  if (!observer) return null;
  const done = observer.record.raters_completed;
  const total = 3;
  if (done >= total) return null;
  const c = ASK_THREE_COPY;
  return (
    <aside className="mt-8 rounded-[16px] border border-accent-text bg-panel p-5 sm:p-6" aria-labelledby="send-raters-title" data-testid="send-raters">
      <p className="eyebrow text-accent-text">{c.eyebrow}</p>
      <h2 id="send-raters-title" className="heading mt-2 text-balance text-[24px] leading-tight">
        {c.heading(done)}
      </h2>
      <Progress done={done} total={total} />
      {done === 0 ? (
        <>
          <p className="mt-4 text-pretty text-[16px] leading-relaxed">{c.why}</p>
          <ul className="mt-3 flex flex-col gap-2.5">
            {c.shows.map((x) => (
              <li key={x.title} className="flex gap-3 text-[15px] leading-snug">
                <span aria-hidden="true" className="mt-[6px] size-2 shrink-0 rounded-full bg-accent-text" />
                <span className="min-w-0">
                  <strong>{x.title}</strong>: {x.text}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-4 text-pretty text-[16px] leading-relaxed">{c.progressText(done)}</p>
      )}
      <div className="mt-5">
        <RaterInvites takeId={takeId} resultKey={resultKey} suggestion={null} compact />
      </div>
    </aside>
  );
}

/** Three segments: answered, and what unlocks at two and three. */
function Progress({ done, total }: { done: number; total: number }) {
  return (
    <div className="mt-3" aria-hidden="true">
      <div className="flex gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full ${i < done ? "bg-accent-text" : "bg-line"}`} />
        ))}
      </div>
      <p className="mt-1.5 text-[13px] text-muted">
        {done} of {total} answered. You get a first look at 2 and the full picture at 3.
      </p>
    </div>
  );
}
